/**
 * Fingerprint OCR inference orchestration (batch create + SSE/poll wait + result mapping).
 */

import {
  fingerprintInferenceAnalysisResultSchema,
  type CreateFingerprintInferenceBatchRequest,
  type FingerprintInferenceAnalysisResult,
  type FingerprintInferenceBatchProgress,
  type FingerprintInferenceBatchStatus,
} from '@cnapse/shared/schemas.js';
import { fingerprintInferenceApi } from '../api/generated/fingerprintInference.api.js';
import { consumeFingerprintInferenceBatchSse } from '../api/sse/index.js';

class FingerprintInferenceService {
  async createFingerprintInferenceBatch(
    data: CreateFingerprintInferenceBatchRequest,
  ): Promise<{
    batchId: string;
    enqueued: boolean;
    done?: boolean;
    progress?: FingerprintInferenceBatchProgress;
  }> {
    const result = await fingerprintInferenceApi.createBatch(data) as {
      batchId: string;
      enqueued?: boolean;
      done?: boolean;
      progress?: FingerprintInferenceBatchProgress;
    };
    return {
      batchId: result.batchId,
      enqueued: result.enqueued ?? false,
      done: result.done,
      progress: result.progress,
    };
  }

  async getFingerprintInferenceBatchStatus(batchId: string): Promise<FingerprintInferenceBatchStatus> {
    return fingerprintInferenceApi.getBatchStatus(batchId) as Promise<FingerprintInferenceBatchStatus>;
  }

  async getFingerprintInferenceBatchProgress(batchId: string): Promise<FingerprintInferenceBatchProgress> {
    return fingerprintInferenceApi.getBatchProgress(batchId) as Promise<FingerprintInferenceBatchProgress>;
  }

  private mapBatchProgressToAnalysis(
    progress: FingerprintInferenceBatchProgress,
  ): FingerprintInferenceAnalysisResult {
    const ocrResults = progress.images
      .filter((img) => img.fingerprint != null && img.fingerprint.length > 0)
      .map((img) => ({
        text: img.fingerprint!,
        confidence: (img.confidence ?? 0) * 100,
        imageId: img.objectKey,
        blurScore: img.blurScore ?? 0,
      }));

    const resolution = progress.resolvedFingerprint
      ? {
          finalValue: progress.resolvedFingerprint.value,
          finalConfidence: progress.resolvedFingerprint.confidence * 100,
          resolutionMethod: progress.resolvedFingerprint.resolutionMethod,
          requiredUserReview: progress.resolvedFingerprint.requiredReview,
          explanation: progress.resolvedFingerprint.explanation,
        }
      : undefined;

    const allImagesLowQuality =
      progress.images.length > 0 &&
      progress.images.every((img) => (img.blurScore ?? 100) < 50);

    const warnings: string[] = [];
    if (progress.phase === 'failed') {
      warnings.push(progress.phaseError ?? 'Batch OCR failed');
    }
    for (const img of progress.images) {
      if (img.ocrErrorMessage) {
        warnings.push(`${img.objectKey}: ${img.ocrErrorMessage}`);
      }
    }

    return fingerprintInferenceAnalysisResultSchema.parse({
      ocrResults,
      resolution,
      allImagesLowQuality,
      warnings: warnings.length > 0 ? warnings : undefined,
    });
  }

  private async getCompletedBatchProgress(
    batchId: string,
  ): Promise<FingerprintInferenceBatchProgress | null> {
    const status = await this.getFingerprintInferenceBatchStatus(batchId);
    if (!status.done) {
      return null;
    }
    return status.progress ?? (await this.getFingerprintInferenceBatchProgress(batchId));
  }

  private async waitForBatchCompletion(
    batchId: string,
    maxWaitMs: number,
  ): Promise<FingerprintInferenceBatchProgress> {
    const immediate = await this.getCompletedBatchProgress(batchId);
    if (immediate) {
      return immediate;
    }

    const ac = new AbortController();
    const deadline = Date.now() + maxWaitMs;
    const timeoutId = setTimeout(() => ac.abort(), maxWaitMs);

    try {
      return await new Promise<FingerprintInferenceBatchProgress>((resolve, reject) => {
        let settled = false;

        const tryComplete = async (): Promise<void> => {
          if (settled) {
            return;
          }
          try {
            const progress = await this.getCompletedBatchProgress(batchId);
            if (progress) {
              settled = true;
              ac.abort();
              resolve(progress);
            }
          } catch (error) {
            if (!settled) {
              settled = true;
              ac.abort();
              reject(error instanceof Error ? error : new Error(String(error)));
            }
          }
        };

        const failTimeout = (): void => {
          if (settled) {
            return;
          }
          settled = true;
          ac.abort();
          reject(new Error('Fingerprint inference timed out waiting for batch completion'));
        };

        void tryComplete();

        const pollId = setInterval(() => {
          if (Date.now() >= deadline) {
            clearInterval(pollId);
            failTimeout();
            return;
          }
          void tryComplete();
        }, 1000);

        const sseLoop = async (): Promise<void> => {
          const { isRealtimeSseEnabled } = await import('../config/realtime.js');
          if (!isRealtimeSseEnabled()) {
            while (!settled && Date.now() < deadline) {
              await tryComplete();
              await new Promise((r) => setTimeout(r, 1000));
            }
            clearInterval(pollId);
            if (!settled && Date.now() >= deadline) {
              failTimeout();
            }
            return;
          }

          while (!settled && Date.now() < deadline) {
            try {
              await consumeFingerprintInferenceBatchSse(batchId, ac.signal, () => {
                void tryComplete();
              });
            } catch {
              if (ac.signal.aborted || settled) {
                break;
              }
              await new Promise((r) => setTimeout(r, 500));
            }
          }
          clearInterval(pollId);
          if (!settled && Date.now() >= deadline) {
            failTimeout();
          }
        };

        void sseLoop();
      });
    } finally {
      clearTimeout(timeoutId);
      ac.abort();
    }
  }

  async runFingerprintInferenceAnalysis(
    data: CreateFingerprintInferenceBatchRequest,
    options?: { maxWaitMs?: number },
  ): Promise<{ data: FingerprintInferenceAnalysisResult }> {
    const maxWaitMs = options?.maxWaitMs ?? 120_000;
    const created = await this.createFingerprintInferenceBatch({
      ...data,
      waitTimeoutMs: 0,
    });

    if (created.progress) {
      return { data: this.mapBatchProgressToAnalysis(created.progress) };
    }

    const { batchId, enqueued, done } = created;
    if (done) {
      const progress = await this.getFingerprintInferenceBatchProgress(batchId);
      return { data: this.mapBatchProgressToAnalysis(progress) };
    }

    if (!enqueued) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    const progress = await this.waitForBatchCompletion(batchId, maxWaitMs);
    return { data: this.mapBatchProgressToAnalysis(progress) };
  }
}

export const fingerprintInferenceService = new FingerprintInferenceService();
